const DATE_INPUT_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

const isValidTimeZone = (timeZone: string): boolean => {
  try {
    new Intl.DateTimeFormat("en-US", { timeZone }).format();
    return true;
  } catch {
    return false;
  }
};

/**
 * Returns the browser's canonical IANA time zone for analytics requests.
 * UTC is used when Intl is unavailable or cannot resolve a valid zone.
 */
export const GetBrowserTimeZone = (): string => {
  try {
    if (
      typeof Intl === "undefined" ||
      typeof Intl.DateTimeFormat !== "function"
    ) {
      return "UTC";
    }

    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone?.trim();
    return timeZone && isValidTimeZone(timeZone) ? timeZone : "UTC";
  } catch {
    return "UTC";
  }
};

/** Formats a Date as YYYY-MM-DD using the browser's local calendar day. */
export const FormatLocalDateInputValue = (date: Date = new Date()): string => {
  if (Number.isNaN(date.getTime())) return "";

  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getDate()).padStart(2, "0"),
  ].join("-");
};

/** Converts an ISO instant to the corresponding local YYYY-MM-DD value. */
export const FormatInstantAsLocalDateInputValue = (
  value: string | Date | null | undefined,
): string => {
  if (!value) return "";
  const date = value instanceof Date ? value : new Date(value);
  return FormatLocalDateInputValue(date);
};

/**
 * Parses a date-only input as local midnight. Native Date parsing treats
 * YYYY-MM-DD as UTC, which can display the previous day west of Greenwich.
 */
export const ParseLocalDateInputValue = (
  value: string | null | undefined,
): Date | null => {
  if (!value) return null;
  const match = DATE_INPUT_PATTERN.exec(value);
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]) - 1;
  const day = Number(match[3]);
  const date = new Date(year, month, day);

  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month ||
    date.getDate() !== day
  ) {
    return null;
  }

  return date;
};

/** Parses date-only values locally and timestamps as absolute instants. */
export const ParseDateForLocalDisplay = (
  value: string | null | undefined,
): Date | null => {
  if (!value) return null;

  if (DATE_INPUT_PATTERN.test(value)) {
    return ParseLocalDateInputValue(value);
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

/** Shows elapsed response time through 30 days, then the local calendar date. */
export const FormatLastResponse = (
  value: string | null | undefined,
  now: number = Date.now(),
): string => {
  const date = ParseDateForLocalDisplay(value);
  if (!date) return "";

  const minute = 60 * 1000;
  const hour = 60 * minute;
  const day = 24 * hour;
  const elapsed = Math.max(0, now - date.getTime());

  if (elapsed > 30 * day) {
    return `Last response on ${date.toLocaleDateString("en-GB", {
      year: "numeric",
      month: "short",
      day: "numeric",
    })}`;
  }

  if (elapsed < minute) return "Last response just now";

  const [unit, duration] =
    elapsed >= 7 * day
      ? (["w", 7 * day] as const)
      : elapsed >= day
        ? (["d", day] as const)
        : elapsed >= hour
          ? (["h", hour] as const)
          : (["m", minute] as const);

  return `Last response ${Math.floor(elapsed / duration)}${unit} ago`;
};
